import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanSearch, Megaphone, FileSignature, Cpu, HandCoins, Scale, ArrowRight, Check, Minus, Globe, Users, Workflow, BellRing, MessageSquare } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AddressSearch from "../components/AddressSearch";
import { usePageSeo } from "../lib/seo";
import { ENTITY_SITE, brokerageNode, agentNode, breadcrumbNode } from "../lib/entityGraph";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const CANON = `${ENTITY_SITE}/services`;
const HERO_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_home-value-estimate/artifacts/1f751a8636e0d8ca_AI%20RES%20RE.jpeg";
const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6 } };

const Kicker = ({ children, light }) => (
  <span className={`eyebrow ${light ? "text-brand-gold" : ""}`}>*{children}*</span>
);

const PILLARS = [
  { icon: ScanSearch, title: "Strategic valuation & pre-market discovery",
    body: "Grounded pricing that combines micro-market comparable analysis with our multi-portal benchmarking engine (Zillow Zestimate® via Bridge, Redfin, Homes.com) and a multi-AI comparative panel — then a turnkey prep plan so your home shows flawlessly from day one.",
    bullets: [
      ["Turnkey home prep & vetted contractors", "Direct access to our trusted network of vetted contractors for pre-sale repairs, cosmetic touch-ups, and inspection prep — eliminating buyer discount leverage before it starts."],
      ["$250 yard prep included", "Complimentary curb-appeal cleanup to maximize exterior presentation and photography framing from day one."],
      ["Prepaid whole-home staging", "Full professional interior staging coordinated and prepaid upfront, with costs reimbursed at the close of escrow — $0 out-of-pocket before closing."],
    ] },
  { icon: Megaphone, title: "High-impact omnichannel marketing",
    body: "A premium media package engineered for offer density, placed everywhere serious buyers look.",
    bullets: [
      ["Cinematic media package", "High-resolution HDR photography, cinematic 4K video walkthroughs, and accurate 2D/3D floor plans."],
      ["Full MLS & mass syndication", "Zillow, Redfin, Realtor.com, Homes.com, social platforms, and local agent networks."],
      ["Broker-hosted access", "Weekend open houses, showing coordination, and secure lockbox management."],
    ] },
  { icon: FileSignature, title: "High-stakes contract & escrow management",
    body: "Handled directly by Broker Louis Fong (CA DRE #01151598) — never handed to a junior team agent.",
    bullets: [
      ["Buyer vetting & negotiation", "Qualification screening, purchase-agreement and counteroffer negotiation, and contingency management."],
      ["Single-agency fiduciary loyalty", "Exclusive representation with zero dual agency."],
      ["Closing oversight", "Municipal tax filings (including San Jose Measure E), title and escrow coordination through a clean close."],
    ] },
];

const PROGRAMS = [
  { name: "1% Full-Service Listing", fee: "1.0% listing fee",
    note: "Buyer-agent compensation set transparently with the seller.",
    includes: "Comprehensive MLS listing, multi-portal syndication, professional media package, disclosures management, open houses, aggressive contract negotiation, and full escrow supervision.", featured: true },
  { name: "2% Direct Sale Program", fee: "2.0% total commission",
    note: "For a buyer who wants your home and has no agent — an unrepresented buyer from your open house, sign call, or online listing.",
    includes: "1% listing fee + 1% direct-buyer coordination. Equitas represents you exclusively (single agency): full C.A.R. contract drafting, mandatory California seller disclosures, BRBC screening to confirm the buyer is unrepresented, neutral escrow coordination, and closing settlement support." },
  { name: "Multi-AI & 3-Portal Net Equity Engine", fee: "Free · No obligation",
    note: "See your numbers before you commit to anything.",
    includes: "Instant 3-portal valuation benchmarking, 3-model AI comparable reasoning (Gemini, OpenAI, Claude), and an itemized net equity sheet reflecting Santa Clara County documentary transfer taxes and closing costs." },
];

const WORKFLOW = [
  { icon: Workflow, title: "Live milestone tracking",
    body: "Real-time transparency from contractor prep and staging installation to disclosure approvals, showing feedback, loan contingencies, and escrow closing." },
  { icon: BellRing, title: "Instant visibility",
    body: "Eliminates phone tag and endless \u201cwhat's happening next?\u201d check-ins by automatically notifying both sellers and buyers the moment each task is completed." },
  { icon: MessageSquare, title: "Superior communication",
    body: "Keeps you measurably more informed than traditional agents relying on scattered text messages and buried email chains." },
];

const OPS = [
  { icon: Globe, title: "Independent, remote-first architecture",
    body: "By operating as a lean, independent brokerage with no expensive commercial leases, no franchise royalties (6%–8%), and no executive layers, we eliminated hundreds of thousands in traditional brick-and-mortar bloat." },
  { icon: Cpu, title: "AI & task automation",
    body: "Modern technology and AI handle repetitive, mundane administrative tasks, document routing, compliance tracking, and marketing deployment — cutting operational time and back-office expense." },
  { icon: HandCoins, title: "Direct savings passed to sellers",
    body: "We don't pocket these operational efficiencies as surplus margin. We pass them directly to homeowners via our 1% listing fee, preserving $30,000 to $60,000+ of your home equity." },
  { icon: Scale, title: "Proportional Silicon Valley economics",
    body: "Selling a $2.5M home does not cost 2.5× more than a $1M home. A 1% fee generates sustainable brokerage revenue while leaving substantial five-figure equity in the seller's pocket." },
  { icon: Users, title: "Word-of-mouth vs. wasteful ad spend",
    body: "Legacy brokerages spend thousands per listing on billboards, mailers, and lead-generation platforms. By consistently protecting seller equity, our business is fueled by direct word-of-mouth and repeat clients." },
];

const COMPARE = [
  ["Listing agent commission", "2.5% – 3.0%", "1.0% listing fee"],
  ["Full-home staging", "Seller pays thousands upfront", "Prepaid upfront, reimbursed at escrow close"],
  ["Yard & curb-appeal prep", "Out-of-pocket cost", "$250 credit included"],
  ["Contractor & repair network", "Seller manages trades alone", "Vetted contractor list & coordination"],
  ["Visual media package", "Varies by agent", "4K video walkthrough, HDR photos & floor plans"],
  ["Transaction communication", "Scattered texts & delayed emails", "Proprietary real-time workflow (~80% fewer calls/emails)"],
  ["Broker-led representation", "Often handed to junior team agents", "Directly with Broker Louis Fong (CA DRE #01151598)"],
  ["Retained equity on a $2.2M sale", "$0 saved (~$66,000 listing fee)", "$44,000 retained by the seller"],
];

const FAQ = [
  ["Does a 1% listing fee compromise on marketing quality or open houses?",
    "No. We provide professional photography, digital floor plans, syndication across the MLS, Zillow, and Redfin, along with broker-hosted open houses. High-quality marketing drives higher offer density — cutting corners would harm your net proceeds."],
  ["How does buyer broker compensation work with a 1% listing?",
    "The 1% fee covers Equitas's listing representation. Any buyer broker compensation is determined collaboratively with you based on active market dynamics and buyer agency agreements."],
  ["Who manages my sale day-to-day?",
    "Every transaction is handled directly by Broker Louis Fong (CA DRE #01151598), with over 30 years of local Silicon Valley real estate experience."],
  ["How does the prepaid staging and yard prep credit work?",
    "We provide an included $250 yard prep credit to sharpen curb appeal prior to launch. For interior staging, we coordinate and prepay the professional staging upfront so your home shows at its absolute best without requiring out-of-pocket cash. The staging expense is simply reimbursed from sales proceeds at the close of escrow."],
  ["How does the proprietary workflow reduce communication noise?",
    "Most real estate friction comes from status uncertainty. Our proprietary workflow provides sellers and buyers with live, centralized milestone tracking for showings, contractor repairs, disclosures, and contingencies. By delivering real-time status updates automatically, we eliminate nearly 80% of routine phone calls and emails while keeping you far better informed than traditional agent communications."],
];

export default function Services() {
  usePageSeo({
    title: "What We Do — Full-Service at a 1% Listing Fee | Equitas Homes",
    description: "Equitas Homes is the Santa Clara County brokerage designed around protecting seller equity: full-service representation at a 1% listing fee plus a 2% Direct Sale program, led directly by Broker Louis Fong (CA DRE #01151598).",
    canonical: CANON,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        brokerageNode(), agentNode(),
        { "@type": "FAQPage", "@id": `${CANON}#faq`,
          mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
        breadcrumbNode(`${CANON}#breadcrumb`, [
          { name: "Home", item: `${ENTITY_SITE}/` },
          { name: "What We Do", item: CANON },
        ]),
      ],
    },
  });

  return (
    <div className="min-h-screen bg-brand-cream paper-grain flex flex-col" data-testid="services-page">
      <Header />
      <main className="flex-1">
        {/* HERO */}
        <section className="bg-brand-emerald text-brand-cream">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-28">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div {...fade}>
                <Kicker light>FULL FIDUCIARY · 1% LISTING FEE</Kicker>
                <h1 className="mt-5 font-serif font-light leading-[1.07] text-4xl sm:text-5xl lg:text-6xl">
                  We are the brokerage designed around <span className="text-brand-gold">protecting seller equity.</span>
                </h1>
                <p className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-brand-cream/80">
                  Full-service real estate representation across Santa Clara &amp; San Mateo counties for a 1% listing fee. We pair 30+ years of local Silicon Valley brokerage expertise with transparent valuation technology — so you keep the equity you spent decades building.
                </p>
                <div className="mt-9 flex flex-col sm:flex-row gap-3">
                  <Link to="/" data-testid="services-hero-primary"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-gold px-6 py-3.5 text-sm font-semibold text-brand-dark transition-colors hover:bg-brand-gold/90">
                    Check your home value &amp; net equity <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/contact" data-testid="services-hero-secondary"
                    className="inline-flex items-center justify-center rounded-full border border-brand-cream/30 px-6 py-3.5 text-sm font-semibold text-brand-cream transition-colors hover:bg-brand-cream/10">
                    Schedule a strategy call with Louis Fong
                  </Link>
                </div>
                <p className="mt-7 text-xs sm:text-sm text-brand-cream/55 tracking-wide">
                  CA DRE #01151598 · Silicon Valley &amp; Peninsula Specialist · $0 Upfront Fees
                </p>
              </motion.div>
              <motion.div {...fade} transition={{ duration: 0.7, delay: 0.1 }} className="relative">
                <img src={HERO_IMG} alt="Equitas Homes AI-driven, real-time home-sale dashboard reviewed by a seller couple in a modern Silicon Valley home"
                  loading="eager" data-testid="services-hero-image"
                  className="w-full rounded-2xl border border-brand-cream/15 shadow-[0_24px_60px_rgba(0,0,0,0.35)] object-cover aspect-[16/10]" />
                <div className="absolute -bottom-4 left-4 right-4 rounded-xl bg-brand-dark/80 backdrop-blur px-4 py-3 border border-brand-cream/10 hidden sm:block">
                  <p className="text-xs text-brand-cream/80 leading-snug"><span className="text-brand-gold font-semibold">Real-time workflow</span> — live milestone tracking from prep to close, ~80% fewer calls &amp; emails.</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* THESIS */}
        <section className="mx-auto max-w-4xl px-5 sm:px-8 py-16 lg:py-24" data-testid="services-thesis">
          <motion.div {...fade}>
            <Kicker>OUR CORE THESIS</Kicker>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-brand-ink">Traditional commissions were built for a different era.</h2>
            <div className="mt-6 space-y-5 text-brand-stone leading-relaxed">
              <p>In a market where median home values regularly surpass $1.8M to $3M+, the traditional 5% to 6% total commission model extracts <strong className="text-brand-ink">$90,000 to $180,000+</strong> from a single home sale.</p>
              <p>Selling a multi-million-dollar home in San Jose, Cupertino, or Palo Alto requires premium marketing, rigorous contract execution, and seasoned negotiation — not an inflated legacy fee structure.</p>
              <p>Equitas Homes was founded on a singular premise: build a lean, data-backed brokerage model that delivers end-to-end fiduciary representation while returning <strong className="text-brand-ink">$30,000 to $60,000+</strong> in retained equity directly to the homeowner.</p>
            </div>
          </motion.div>
        </section>

        {/* WHAT WE DO — 3 PILLARS */}
        <section className="bg-brand-sand/60 border-y border-brand-hairline">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24" data-testid="services-pillars">
            <motion.div {...fade} className="max-w-2xl">
              <Kicker>FULL-SERVICE EXECUTION</Kicker>
              <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-brand-ink">Hands-on representation from pre-market preparation to escrow closing.</h2>
              <p className="mt-4 text-brand-stone leading-relaxed">We are not a passive flat-fee MLS listing service. Every listing receives complete, active representation from Broker Louis Fong.</p>
            </motion.div>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {PILLARS.map((p, i) => (
                <motion.div key={p.title} {...fade} transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="rounded-2xl bg-white border border-brand-hairline p-7 shadow-[0_4px_20px_rgba(11,30,140,0.04)]" data-testid="services-pillar-card">
                  <div className="h-11 w-11 rounded-xl bg-brand-emerald/10 flex items-center justify-center"><p.icon className="h-5 w-5 text-brand-emerald" /></div>
                  <h3 className="mt-4 font-serif text-xl text-brand-ink">{p.title}</h3>
                  <p className="mt-2 text-sm text-brand-stone leading-relaxed">{p.body}</p>
                  {p.bullets && (
                    <ul className="mt-4 space-y-3 border-t border-brand-hairline pt-4">
                      {p.bullets.map(([label, text]) => (
                        <li key={label} className="flex gap-2.5" data-testid="services-pillar-bullet">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-emerald" />
                          <span className="text-sm text-brand-stone leading-relaxed"><strong className="text-brand-ink font-semibold">{label}:</strong> {text}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PROPRIETARY REAL-TIME CLIENT WORKFLOW */}
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24" data-testid="services-workflow">
          <motion.div {...fade} className="relative overflow-hidden rounded-3xl bg-brand-dark text-brand-cream p-8 sm:p-12 lg:p-14 border border-brand-cream/10">
            <div className="max-w-3xl">
              <Kicker light>PROPRIETARY TECHNOLOGY</Kicker>
              <h2 className="mt-2 font-serif font-light text-3xl sm:text-4xl lg:text-[2.75rem] leading-tight">A proprietary workflow that replaces chaos with clarity.</h2>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-brand-cream/80">
                Stay completely informed in real time — with <span className="text-brand-gold font-semibold">nearly 80% fewer phone calls and emails.</span>
              </p>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {WORKFLOW.map((w, i) => (
                <div key={w.title} className="rounded-2xl border border-brand-cream/15 bg-brand-cream/[0.04] p-6" data-testid="services-workflow-card">
                  <div className="h-11 w-11 rounded-xl bg-brand-gold/15 flex items-center justify-center"><w.icon className="h-5 w-5 text-brand-gold" /></div>
                  <h3 className="mt-4 font-serif text-lg text-brand-cream">{w.title}</h3>
                  <p className="mt-2 text-sm text-brand-cream/75 leading-relaxed">{w.body}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* WHAT WE OFFER — PROGRAMS */}
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24" data-testid="services-programs">
          <motion.div {...fade} className="max-w-2xl">
            <Kicker>OUR PROGRAMS</Kicker>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-brand-ink">Structured programs built around your exact transaction.</h2>
          </motion.div>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {PROGRAMS.map((p, i) => (
              <motion.div key={p.name} {...fade} transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`rounded-2xl border p-7 flex flex-col ${p.featured ? "border-brand-emerald bg-brand-emeraldLight/40 shadow-[0_8px_30px_rgba(11,30,140,0.08)]" : "border-brand-hairline bg-white"}`}
                data-testid="services-program-card">
                {p.featured && <span className="self-start mb-3 rounded-full bg-brand-emerald px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-brand-cream">Most chosen</span>}
                <h3 className="font-serif text-xl text-brand-ink">{p.name}</h3>
                <p className="mt-2 font-mono text-2xl font-bold text-brand-emerald">{p.fee}</p>
                <p className="mt-3 text-sm text-brand-stone leading-relaxed italic">{p.note}</p>
                <p className="mt-4 text-sm text-brand-stone leading-relaxed border-t border-brand-hairline pt-4">{p.includes}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HOW WE OFFER IT — OPERATIONAL MODEL */}
        <section className="bg-brand-emerald text-brand-cream">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24" data-testid="services-ops">
            <motion.div {...fade} className="max-w-3xl">
              <Kicker light>THE OPERATIONAL MODEL</Kicker>
              <h2 className="mt-2 font-serif font-light text-3xl sm:text-4xl">How we deliver full-service representation for 1%.</h2>
              <p className="mt-4 text-brand-cream/75 leading-relaxed">A fair question: <em>"If other agents charge 2.5% to 3%, what makes this sustainable?"</em> Here is exactly how the economics work.</p>
            </motion.div>
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {OPS.map((o, i) => (
                <motion.div key={o.title} {...fade} transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="rounded-2xl border border-brand-cream/15 bg-brand-dark/40 p-7" data-testid="services-ops-card">
                  <div className="h-11 w-11 rounded-xl bg-brand-cream/10 flex items-center justify-center"><o.icon className="h-5 w-5 text-brand-gold" /></div>
                  <h3 className="mt-4 font-serif text-xl text-brand-cream">{o.title}</h3>
                  <p className="mt-2 text-sm text-brand-cream/75 leading-relaxed">{o.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* COMPARISON TABLE */}
        <section className="mx-auto max-w-5xl px-5 sm:px-8 py-16 lg:py-24" data-testid="services-compare">
          <motion.div {...fade} className="max-w-2xl">
            <Kicker>THE DIFFERENCE</Kicker>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-brand-ink">Traditional brokerage vs. Equitas Homes.</h2>
          </motion.div>
          <motion.div {...fade} className="mt-8 overflow-x-auto rounded-2xl border border-brand-hairline bg-white">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-brand-hairline bg-brand-sand/50">
                  <th className="text-left px-5 py-4 font-semibold text-brand-ink">Service</th>
                  <th className="text-left px-5 py-4 font-semibold text-brand-stone">Traditional Brokerage</th>
                  <th className="text-left px-5 py-4 font-semibold text-brand-emerald">Equitas Homes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-hairline">
                {COMPARE.map(([metric, trad, eq]) => (
                  <tr key={metric} data-testid="services-compare-row">
                    <td className="px-5 py-4 font-medium text-brand-ink align-top">{metric}</td>
                    <td className="px-5 py-4 text-brand-stone align-top"><span className="inline-flex items-start gap-1.5"><Minus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-stone/50" />{trad}</span></td>
                    <td className="px-5 py-4 font-semibold text-brand-ink align-top"><span className="inline-flex items-start gap-1.5"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-emerald" />{eq}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </section>

        {/* FAQ */}
        <section className="bg-brand-sand/60 border-y border-brand-hairline">
          <div className="mx-auto max-w-3xl px-5 sm:px-8 py-20" data-testid="services-faq">
            <motion.div {...fade}>
              <Kicker>COMMON QUESTIONS</Kicker>
              <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-brand-ink">Questions sellers ask us.</h2>
            </motion.div>
            <div className="mt-8" data-testid="services-faq-accordion">
              <Accordion type="single" collapsible>
                {FAQ.map(([q, a], i) => (
                  <AccordionItem key={i} value={`svc-faq-${i}`} className="border-brand-hairline" data-testid="services-faq-item">
                    <AccordionTrigger className="text-left font-serif text-lg text-brand-ink hover:text-brand-emerald" data-testid={`services-faq-trigger-${i}`}>
                      {q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-brand-stone leading-relaxed">{a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* BOTTOM CTA */}
        <section className="bg-brand-dark text-brand-cream">
          <div className="mx-auto max-w-4xl px-5 sm:px-8 py-20 lg:py-24 text-center" data-testid="services-cta">
            <motion.div {...fade}>
              <Kicker light>SEE YOUR NUMBERS</Kicker>
              <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-tight">Protect your equity. See your numbers.</h2>
              <div className="mt-9 text-left max-w-2xl mx-auto">
                <AddressSearch />
              </div>
              <p className="mt-6 text-sm text-brand-cream/60">
                Prefer a human conversation? <Link to="/contact" className="text-brand-gold hover:underline" data-testid="services-cta-contact">Schedule a consultation with Louis Fong</Link>.
              </p>
              <p className="mt-8 text-xs text-brand-cream/45 leading-relaxed max-w-2xl mx-auto">
                All brokerage commissions and fees are negotiable and are not set by law. CA DRE #01151598.
              </p>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
